import type { Role } from "@gosaas/types/rbac";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { useFetchTeamMembers } from "@/components/queries/team";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { apiClient, requestWithHandling } from "@/utils/api";

interface ChangeRoleProps {
	memberId: string;
}

export function ChangeRole({ memberId }: ChangeRoleProps) {
	const { teamID, refresh, teams } = useTeam();
	const { teamMembers, refetchTeamMembers } = useFetchTeamMembers(
		teamID as string,
	);
	const team = teams.find((t) => t.id === teamID);
	if (!team) {
		return null;
	}

	// Find the specific member's current role
	const member = teamMembers?.find((m) => m.id === memberId);
	const rawRole = member?.role;

	// Normalize role to match Role type (capitalize first letter)
	const normalizeRole = (role: string | undefined): Role | null => {
		if (!role) return null;
		// Find matching role case-insensitively
		const roles: Role[] = ["Owner", "Admin", "Billing", "Member"];
		const normalized = roles.find(
			(r) => r.toLowerCase() === role.toLowerCase(),
		);
		return normalized || null;
	};

	const currentRole = normalizeRole(rawRole);
	const roles: Role[] = ["Owner", "Admin", "Billing", "Member"];

	const handleRoleChange = async (newRole: Role) => {
		// Don't update if the role hasn't changed
		if (newRole === currentRole) {
			return;
		}

		try {
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamMemberUpdate(teamID as string, {
					id: memberId,
					role: newRole,
				}),
			);

			if (response.code === "SUCCESS") {
				toast.success("Role updated successfully");
				refetchTeamMembers();
				await refresh();
			} else {
				throw new Error(response.message || "Failed to update role");
			}
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "An unexpected error occurred";
			toast.error("Failed to update role", {
				description: message,
			});
		}
	};

	if (!currentRole) {
		return <span className="text-muted-foreground">Loading...</span>;
	}

	return (
		<Select
			value={currentRole}
			onValueChange={(v) => {
				if (v) handleRoleChange(v as Role);
			}}
		>
			<SelectTrigger className="w-[180px]">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{roles.map((role) => (
					<SelectItem key={role} value={role}>
						{role}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}
